import * as React from "react";
import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  ArrowDownToLine,
  Building2,
  History,
  Lock,
  MoreHorizontal,
  Pencil,
  Plug,
  Plus,
  Power,
  RefreshCw,
  Search,
  Trash2,
  Unlock,
} from "Components/ui/icons";

import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  DataTable,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  Page,
  PageHeader,
  StatCard,
  StatusBadge,
  Tooltip,
} from "Components/ui";
import {
  ConnectionDim,
  ConnectTeleopdButton,
  isConnected,
  NotConnected,
  TenantIdentity,
} from "Components/Tenants";
import { useAuth } from "hooks/useAuth";
import { formatRelative } from "utils/format";
import AuditLogTable from "Containers/Admin/AuditLogs/AuditLogTable";
import useClientsData, { useClientBillingStatuses } from "./data";
import ClientFormDialog from "./ClientFormDialog";
import { Client, ClientBillingMap, ClientPayload, ClientStage } from "./types";

const LOCKED_HINT = "Locked until the production release";

interface RowActions {
  canWrite: boolean;
  canDelete: boolean;
  canConfigure: boolean;
  /** billing/configure: issuing TeleOpd keys lives on the billing Connection tab. */
  canConnect: boolean;
  onEdit: (client: Client) => void;
  onToggleStatus: (client: Client) => void;
  onStage: (client: Client, stage: ClientStage) => void;
  onDelete: (client: Client) => void;
  onHistory: (client: Client) => void;
}

const DeploymentCell: React.FC<{ client: Client }> = ({ client }) =>
  client.deployment ? (
    <div className="whitespace-nowrap font-mono text-xs">
      <p>{client.deployment.server}</p>
      <p className="text-muted-foreground">
        pm2 {client.deployment.app}
        {client.deployment.port ? ` · :${client.deployment.port}` : ""}
      </p>
    </div>
  ) : (
    <span className="text-muted-foreground">—</span>
  );

const buildColumns = (
  stage: ClientStage,
  billingByClient: ClientBillingMap | null,
  actions: RowActions
): ColumnDef<Client>[] => {
  const columns: ColumnDef<Client>[] = [
    {
      id: "tenant",
      header: "Tenant",
      cell: ({ row }) => (
        <TenantIdentity
          name={row.original.name}
          slug={row.original.slug}
          uri={row.original.uri}
          muted={!isConnected(row.original.link)}
        />
      ),
    },
    {
      id: "deployment",
      header: "Server · pm2",
      cell: ({ row }) => (
        <ConnectionDim link={row.original.link}>
          <DeploymentCell client={row.original} />
        </ConnectionDim>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => {
        const { link, lifecycleStatus } = row.original;
        return (
          <div className="space-y-1">
            <ConnectionDim link={link}>
              <StatusBadge domain="tenant" status={lifecycleStatus} />
            </ConnectionDim>
            {link && isConnected(link) ? (
              <p className="whitespace-nowrap text-xs text-muted-foreground">
                TeleOpd synced {formatRelative(link.lastSyncAt)}
              </p>
            ) : (
              <NotConnected className="block text-xs" />
            )}
          </div>
        );
      },
    },
  ];

  if (billingByClient) {
    columns.push({
      id: "billing",
      header: "Billing",
      cell: ({ row }) => {
        const billing = billingByClient[row.original._id];
        if (!billing) {
          return <span className="text-muted-foreground">—</span>;
        }
        return (
          <ConnectionDim link={row.original.link}>
            <StatusBadge domain="billing" status={billing.status} />
          </ConnectionDim>
        );
      },
    });
  }

  columns.push({
    id: "actions",
    header: "Actions",
    meta: { align: "right" },
    cell: ({ row }) => {
      const client = row.original;
      // Not connected: no actions except the next step towards connecting it. Locked clients
      // can't be connected (SAP won't issue their keys), so that step is making them actionable.
      if (!isConnected(client.link)) {
        let step: React.ReactNode = <NotConnected />;
        if (stage === "locked" && actions.canConfigure) {
          step = (
            <Button
              size="sm"
              variant="primary-ghost"
              leftIcon={<Unlock />}
              onClick={() => actions.onStage(client, "beta")}
            >
              Make actionable
            </Button>
          );
        } else if (stage === "beta" && actions.canConnect) {
          step = <ConnectTeleopdButton tenantId={client._id} />;
        }
        return <div className="flex items-center justify-end">{step}</div>;
      }

      const links = (
        <>
          <Button asChild size="sm" variant="ghost">
            <Link to={`/clients/${client._id}/modules`}>Modules</Link>
          </Button>
          <Button asChild size="sm" variant="ghost">
            <Link to={`/billing/${client._id}`}>Billing</Link>
          </Button>
        </>
      );

      if (stage === "locked") {
        return (
          <div className="flex items-center justify-end gap-1">
            {links}
            {actions.canConfigure ? (
              <Button
                size="sm"
                variant="primary-ghost"
                leftIcon={<Unlock />}
                onClick={() => actions.onStage(client, "beta")}
              >
                Make actionable
              </Button>
            ) : (
              <Tooltip content={LOCKED_HINT}>
                <span className="inline-flex px-2 text-subtle-foreground">
                  <Lock className="h-4 w-4" aria-label={LOCKED_HINT} />
                </span>
              </Tooltip>
            )}
            <IconButton
              label="Audit history"
              icon={<History />}
              size="sm"
              variant="ghost"
              onClick={() => actions.onHistory(client)}
            />
          </div>
        );
      }

      return (
        <div className="flex items-center justify-end gap-1">
          {links}
          {actions.canWrite && (
            <IconButton
              label="Edit"
              icon={<Pencil />}
              size="sm"
              variant="ghost"
              onClick={() => actions.onEdit(client)}
            />
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                label="More actions"
                icon={<MoreHorizontal />}
                size="sm"
                variant="ghost"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{client.slug}</DropdownMenuLabel>
              <DropdownMenuItem icon={<History />} onSelect={() => actions.onHistory(client)}>
                Audit history
              </DropdownMenuItem>
              {actions.canWrite && (
                <DropdownMenuItem icon={<Power />} onSelect={() => actions.onToggleStatus(client)}>
                  {client.lifecycleStatus === "active" ? "Disable" : "Enable"}
                </DropdownMenuItem>
              )}
              {actions.canConfigure && (
                <DropdownMenuItem
                  icon={<ArrowDownToLine />}
                  onSelect={() => actions.onStage(client, "locked")}
                >
                  Move to Locked
                </DropdownMenuItem>
              )}
              {actions.canDelete && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    tone="danger"
                    icon={<Trash2 />}
                    onSelect={() => actions.onDelete(client)}
                  >
                    Delete
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    },
  });

  return columns;
};

const Clients: React.FC = () => {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("tenantManagement", "write");
  const canDelete = hasPermission("tenantManagement", "delete");
  const canConfigure = hasPermission("tenantManagement", "configure");
  const canReadBilling = hasPermission("billing", "read");
  const canConnect = hasPermission("billing", "configure");

  const data = useClientsData();
  const { billingByClient } = useClientBillingStatuses(canReadBilling);

  const [formOpen, setFormOpen] = React.useState<boolean>(false);
  const [editing, setEditing] = React.useState<Client | null>(null);
  const [stageTarget, setStageTarget] = React.useState<{
    client: Client;
    stage: ClientStage;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = React.useState<Client | null>(null);
  const [historyClient, setHistoryClient] = React.useState<Client | null>(null);

  const knownServers = React.useMemo(
    () =>
      Array.from(
        new Set(
          data.clients
            .map((client) => (client.deployment ? client.deployment.server : ""))
            .filter(Boolean)
        )
      ).sort(),
    [data.clients]
  );

  // The server refuses a slug change too; this just explains it up front.
  const slugLocked = React.useMemo(() => {
    if (!editing) return false;
    const billing = billingByClient[editing._id];
    return Boolean(
      billing && (billing.link.linked || billing.status !== "no_billing" || billing.lastPaymentAt)
    );
  }, [editing, billingByClient]);

  // `mutate` is stable across renders; the mutation object is not.
  const toggleStatus = data.statusMutation.mutate;
  const actions: RowActions = React.useMemo(
    () => ({
      canWrite,
      canDelete,
      canConfigure,
      canConnect,
      onEdit: (client) => {
        setEditing(client);
        setFormOpen(true);
      },
      onToggleStatus: (client) =>
        toggleStatus({
          id: client._id,
          status: client.lifecycleStatus === "active" ? "disabled" : "active",
        }),
      onStage: (client, stage) => setStageTarget({ client, stage }),
      onDelete: (client) => setDeleteTarget(client),
      onHistory: (client) => setHistoryClient(client),
    }),
    [canWrite, canDelete, canConfigure, canConnect, toggleStatus]
  );

  const billingMap = canReadBilling ? billingByClient : null;
  const actionableColumns = React.useMemo(
    () => buildColumns("beta", billingMap, actions),
    [billingMap, actions]
  );
  const lockedColumns = React.useMemo(
    () => buildColumns("locked", billingMap, actions),
    [billingMap, actions]
  );

  const submitForm = async (payload: ClientPayload) => {
    if (editing) {
      await data.updateMutation.mutateAsync({ id: editing._id, payload });
    } else {
      await data.createMutation.mutateAsync(payload);
    }
  };

  const searching = data.search.trim().length > 0;

  return (
    <Page width="wide">
      <PageHeader
        title="Tenants"
        description="Every TeleOpd deployment SAP knows about. Until the production release only Actionable tenants can be changed, and tenants whose TeleOpd isn't connected are greyed out."
        actions={
          <>
            <Button
              variant="secondary"
              leftIcon={<RefreshCw />}
              loading={data.fetching && !data.loading}
              onClick={() => data.refetch()}
            >
              Refresh
            </Button>
            {canWrite && (
              <Button
                leftIcon={<Plus />}
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                Add tenant
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Tenants"
          value={data.counts.total}
          hint="TeleOpd deployments"
          icon={<Building2 />}
          loading={data.loading}
        />
        <StatCard
          label="Actionable"
          value={data.counts.actionable}
          hint="Beta: can be changed now"
          icon={<Unlock />}
          tone="primary"
          loading={data.loading}
        />
        <StatCard
          label="Locked"
          value={data.counts.locked}
          hint="Until the production release"
          icon={<Lock />}
          loading={data.loading}
        />
        <StatCard
          label="Connected"
          value={data.counts.connected}
          hint="TeleOpd synced with SAP"
          icon={<Plug />}
          tone="success"
          loading={data.loading}
        />
      </div>

      <div className="mt-6 max-w-md">
        <Input
          value={data.search}
          onChange={(event) => data.setSearch(event.target.value)}
          placeholder="Search name, pm2 app, URL, server or contact…"
          leading={<Search />}
          aria-label="Search tenants"
        />
      </div>

      {data.error ? (
        <Card className="mt-6">
          <ErrorState
            title="Tenants didn't load"
            description="Check your connection and try again."
            action={
              <Button variant="secondary" onClick={() => data.refetch()}>
                Try again
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="mt-6 space-y-6">
          <Card>
            <CardHeader
              title="Actionable"
              description="Beta: connect a tenant's TeleOpd first; then its billing, modules and record can be changed."
              actions={<StatusBadge domain="stage" status="beta" />}
            />
            <div className="px-5 pb-5 pt-4">
              <DataTable
                columns={actionableColumns}
                data={data.groups.actionable}
                loading={data.loading}
                getRowId={(row) => row._id}
                enableSorting={false}
                caption="Actionable tenants"
                empty={
                  <EmptyState
                    compact
                    icon={<Unlock />}
                    title={searching ? "No actionable tenant matches" : "No actionable tenants"}
                    description={
                      searching
                        ? "Try another name, pm2 app or URL."
                        : "Move a tenant out of Locked to start working on it."
                    }
                  />
                }
              />
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Locked"
              description="Read-only until the production release; changes are refused. Make a tenant actionable to connect its TeleOpd."
              actions={<Badge tone="neutral">Until production release</Badge>}
            />
            <div className="px-5 pb-5 pt-4">
              <DataTable
                columns={lockedColumns}
                data={data.groups.locked}
                loading={data.loading}
                getRowId={(row) => row._id}
                enableSorting={false}
                caption="Locked tenants"
                empty={
                  <EmptyState
                    compact
                    icon={<Lock />}
                    title={searching ? "No locked tenant matches" : "No locked tenants"}
                    description={searching ? "Try another name, pm2 app or URL." : undefined}
                  />
                }
              />
            </div>
          </Card>
        </div>
      )}

      <ClientFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditing(null);
        }}
        client={editing}
        slugLocked={slugLocked}
        knownServers={knownServers}
        saving={data.createMutation.isLoading || data.updateMutation.isLoading}
        onSubmit={submitForm}
      />

      <ConfirmDialog
        open={Boolean(stageTarget)}
        onOpenChange={(open) => {
          if (!open) setStageTarget(null);
        }}
        tone="primary"
        title={
          stageTarget && stageTarget.stage === "beta"
            ? `Make ${stageTarget.client.name} (${stageTarget.client.slug}) actionable?`
            : `Lock ${
                stageTarget ? `${stageTarget.client.name} (${stageTarget.client.slug})` : ""
              }?`
        }
        description={
          stageTarget && stageTarget.stage === "beta"
            ? "SAP will accept changes to its billing, TeleOpd connection and modules. Keep only instances you are testing on here."
            : "SAP will refuse every change to it until it is made actionable again. Nothing changes on the TeleOpd instance."
        }
        confirmLabel={stageTarget && stageTarget.stage === "beta" ? "Make actionable" : "Lock"}
        onConfirm={async () => {
          if (stageTarget) {
            await data.stageMutation.mutateAsync({
              id: stageTarget.client._id,
              stage: stageTarget.stage,
            });
          }
        }}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={`Delete ${deleteTarget ? deleteTarget.name : "tenant"}?`}
        description="Only tenants without billing records or a TeleOpd connection can be deleted. Its module settings are removed too."
        confirmLabel="Delete tenant"
        confirmText={deleteTarget ? deleteTarget.slug : undefined}
        onConfirm={async () => {
          if (deleteTarget) {
            await data.deleteMutation.mutateAsync(deleteTarget);
          }
        }}
      />

      <Dialog
        open={Boolean(historyClient)}
        onOpenChange={(open) => {
          if (!open) setHistoryClient(null);
        }}
      >
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader
            title={historyClient ? `Audit history: ${historyClient.name}` : "Audit history"}
            description={
              historyClient ? `pm2 ${historyClient.slug} · ${historyClient.uri}` : undefined
            }
          />
          <DialogBody>
            {historyClient && (
              <AuditLogTable
                hideTitle
                maxHeight="60vh"
                query={{ targetId: historyClient._id, limit: 200 }}
              />
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </Page>
  );
};

export default Clients;
